import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  COMMENT_REACTION_ADD,
  COMMENT_REACTION_REMOVE,
} from '../../../utils/apollo/conversations';
import {
  ICommentReactionAddMutation,
  ICommentReactionAddMutationVariables,
  ICommentReactionRemoveMutation,
  ICommentReactionRemoveMutationVariables,
  IConversationEntityInput,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../utils/types';
import { useAppDispatch } from '../../../redux/hooks';
import { updateThread } from '../../../redux/slices/conversationThreads';

interface IProps {
  commentId: string;
  entity: IConversationEntityInput;
  threadId: string;
}

const useChangeCommentReaction = (args: IProps) => {
  const dispatch = useAppDispatch();

  const handleNewReaction = ({
    conversationCommentReactionAdd,
  }: ICommentReactionAddMutation) => {
    const { errors, thread } = conversationCommentReactionAdd;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(thread);
    dispatch(updateThread(thread));
  };
  const handleRemovedReaction = ({
    conversationCommentReactionDelete,
  }: ICommentReactionRemoveMutation) => {
    const { errors, thread } = conversationCommentReactionDelete;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(thread);
    dispatch(updateThread(thread));
  };

  const [addCommentReaction] = useMutation<
    ICommentReactionAddMutation,
    ICommentReactionAddMutationVariables
  >(COMMENT_REACTION_ADD, {
    onCompleted: handleNewReaction,
  });

  const [removeCommentReaction] = useMutation<
    ICommentReactionRemoveMutation,
    ICommentReactionRemoveMutationVariables
  >(COMMENT_REACTION_REMOVE, {
    onCompleted: handleRemovedReaction,
  });

  const handleAddReaction = useCallback(
    (emojiKey: string) => {
      return addCommentReaction({
        variables: {
          input: {
            ...args,
            emojiKey,
          },
        },
      });
    },
    [addCommentReaction, args],
  );
  const handleRemoveReaction = useCallback(
    (emojiKey: string) => {
      return removeCommentReaction({
        variables: {
          input: {
            ...args,
            emojiKey,
          },
        },
      });
    },
    [args, removeCommentReaction],
  );

  return {
    handleAddReaction,
    handleRemoveReaction,
  };
};

export default useChangeCommentReaction;
